// §4.10 What-if sandbox, the builder (`/planning/scenarios/new`, P3-J J-4,
// D-472, D-474): a name and the hypothesis rows, each kind with its own fields
// and a remove control, at most 20. Each row is checked as the contract will
// check it before anything is sent; Create and solve posts row 80 and opens the
// result, where the worker's solve shows. Times are typed in the facility's
// zone. Written on `planning.scenario.manage`; a phone reads scenarios and
// builds none.

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { EmptyState } from "../../../../components/states";
import { toast } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { pickableWorkCenters } from "../../../../lib/work-centers";
import { PlanningHeader } from "../components/planning-header";
import { ProductPicker } from "../components/product-picker";
import { useCalendarWorkCenters, useShiftNames } from "../hooks/calendar-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { useProductIndex } from "../hooks/planning-queries";
import {
  HYPOTHESIS_KINDS,
  SCENARIO_MAX_HYPOTHESES,
  SCENARIO_NAME_MAX,
  createScenario,
  useScenarioOrder,
  useScenarioOrders,
  useScenarioRefresh,
  type ScenarioHypothesisKind,
} from "../hooks/scenario-queries";
import { workCenterLabel } from "../lib/calendar-lib";
import { useOffline } from "../lib/planning-lib";
import {
  checkBuilder,
  emptyDraft,
  scenarioProblem,
  type DraftField,
  type DraftIssue,
  type HypothesisDraft,
} from "../lib/scenario-lib";
import { todayAt, zoneOffset } from "../lib/schedule-lib";
import { usePlanningFacility } from "../planning-layout";

type Issues = Partial<Record<DraftField, DraftIssue>>;

export function PlanningScenarioBuilderScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const offline = useOffline();
  const refresh = useScenarioRefresh();
  const zone = facility.timezone;
  const today = todayAt(zone);

  const [name, setName] = useState("");
  const [drafts, setDrafts] = useState<HypothesisDraft[]>(() => [
    emptyDraft("machine_down", today),
  ]);
  const [nextKind, setNextKind] = useState<ScenarioHypothesisKind>("add_shift");
  const [checked, setChecked] = useState(false);
  const [pending, setPending] = useState(false);
  const [serverError, setServerError] = useState<{ text: string; row?: number } | undefined>();

  const workCenters = useCalendarWorkCenters(facility.id);
  const pickable = useMemo(() => pickableWorkCenters(workCenters.data), [workCenters.data]);
  const shiftNames = useShiftNames(facility.id, perms.has("production.read"));
  const needsOrders = drafts.some((d) => d.kind === "drop_order");
  const orders = useScenarioOrders(facility.id, needsOrders);

  const check = checkBuilder(facility.id, name, drafts, zone);
  const rowIssues = (i: number): Issues => (checked && !check.ok ? (check.rows.get(i) ?? {}) : {});

  const header = (
    <PlanningHeader
      title={t("planning.scenarios.builder.title")}
      backTo={{ label: t("planning.scenarios.result.back"), to: "/planning/scenarios" }}
    />
  );
  const back = () => {
    void navigate({ to: "/planning/scenarios" });
  };

  if (!desktop) {
    return (
      <section className="planning-screen" data-testid="planning-scenario-builder">
        {header}
        <EmptyState
          title={t("planning.scenarios.builder.title")}
          body={t("planning.scenarios.builder.phone")}
          ctaLabel={t("planning.scenarios.result.back")}
          onCta={back}
        />
      </section>
    );
  }

  const update = (i: number, next: HypothesisDraft) => {
    setDrafts((prev) => prev.map((d, j) => (j === i ? next : d)));
  };

  const submit = async () => {
    setChecked(true);
    setServerError(undefined);
    if (!check.ok) return;
    setPending(true);
    try {
      const accepted = await createScenario(check.body);
      await refresh(facility.id);
      void navigate({
        to: "/planning/scenarios/$scenarioId",
        params: { scenarioId: accepted.scenario_id },
      });
    } catch (err) {
      const p = scenarioProblem(err);
      const text = t(`planning.scenarios.problem.${p.key}`, p.values);
      const row = typeof p.values["n"] === "number" ? p.values["n"] - 1 : undefined;
      setServerError(row !== undefined ? { text, row } : { text });
      toast.error(text);
    } finally {
      setPending(false);
    }
  };

  const nameIssue = checked && !check.ok && check.nameIssue;
  const offset = zoneOffset(Date.now(), zone);

  return (
    <section className="planning-screen" data-testid="planning-scenario-builder">
      {header}
      <Card>
        <Card.Body>
          <Field label={t("planning.scenarios.builder.name")} wide>
            <input
              type="text"
              value={name}
              maxLength={SCENARIO_NAME_MAX}
              placeholder={t("planning.scenarios.builder.namePlaceholder")}
              aria-invalid={nameIssue}
              data-testid="planning-scenario-builder-name"
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </Field>
          {nameIssue ? (
            <p className="planning-bad" data-testid="planning-scenario-builder-name-issue">
              {t("planning.scenarios.builder.issue.name")}
            </p>
          ) : null}
          <p className="planning-hint">{t("planning.scenarios.builder.zoneNote", { offset })}</p>
        </Card.Body>
      </Card>
      <h2 className="planning-subhead">{t("planning.scenarios.builder.changes")}</h2>
      <ol className="planning-scenario-rows" data-testid="planning-scenario-builder-rows">
        {drafts.map((draft, i) => (
          <li
            // A row is identified by its place; removing one re-keys the rest.
            key={`${String(i)}-${draft.kind}`}
            className="planning-scenario-row"
            data-testid="planning-scenario-builder-row"
            data-kind={draft.kind}
          >
            <div className="planning-scenario-row-head">
              <strong>
                {t("planning.scenarios.builder.row", { n: i + 1 })}:{" "}
                {t(`planning.scenarios.kind.${draft.kind}`)}
              </strong>
              <Button
                size="sm"
                aria-label={t("planning.scenarios.builder.removeAria", { n: i + 1 })}
                data-testid="planning-scenario-builder-remove"
                onClick={() => {
                  setDrafts((prev) => prev.filter((_, j) => j !== i));
                }}
              >
                {t("planning.scenarios.builder.remove")}
              </Button>
            </div>
            <DraftFields
              draft={draft}
              issues={rowIssues(i)}
              onChange={(next) => {
                update(i, next);
              }}
              workCenters={pickable.map((wc) => ({
                id: wc.id,
                label: workCenterLabel(wc, i18n.language),
              }))}
              shiftNames={shiftNames}
              orders={orders.data ?? []}
              ordersError={orders.error !== null}
            />
            {serverError?.row === i ? (
              <p
                className="planning-bad"
                role="alert"
                data-testid="planning-scenario-builder-row-error"
              >
                {serverError.text}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
      {drafts.length === 0 && checked ? (
        <p className="planning-bad" data-testid="planning-scenario-builder-empty">
          {t("planning.scenarios.builder.empty")}
        </p>
      ) : null}
      <div className="planning-actions planning-scenario-add">
        <Field label={t("planning.scenarios.builder.addKind")}>
          <Select
            value={nextKind}
            data-testid="planning-scenario-builder-kind"
            onChange={(e) => {
              setNextKind(e.target.value as ScenarioHypothesisKind);
            }}
          >
            {HYPOTHESIS_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`planning.scenarios.kind.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Button
          disabled={drafts.length >= SCENARIO_MAX_HYPOTHESES}
          data-testid="planning-scenario-builder-add"
          onClick={() => {
            setDrafts((prev) => [...prev, emptyDraft(nextKind, today)]);
          }}
        >
          {t("planning.scenarios.builder.add")}
        </Button>
        {drafts.length >= SCENARIO_MAX_HYPOTHESES ? (
          <span className="planning-hint">
            {t("planning.scenarios.builder.limit", { count: SCENARIO_MAX_HYPOTHESES })}
          </span>
        ) : null}
      </div>
      {serverError !== undefined && serverError.row === undefined ? (
        <p className="planning-bad" role="alert" data-testid="planning-scenario-builder-error">
          {serverError.text}
        </p>
      ) : null}
      {checked && !check.ok && check.rows.size > 0 ? (
        <p className="planning-bad" data-testid="planning-scenario-builder-fix">
          {t("planning.scenarios.builder.fixErrors")}
        </p>
      ) : null}
      {offline ? <p className="planning-note">{t("planning.common.offlineWrites")}</p> : null}
      <div className="planning-actions">
        <Button disabled={pending} onClick={back} data-testid="planning-scenario-builder-cancel">
          {t("planning.scenarios.builder.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={pending || offline}
          data-testid="planning-scenario-builder-submit"
          onClick={() => void submit()}
        >
          {pending
            ? t("planning.scenarios.builder.submitting")
            : t("planning.scenarios.builder.submit")}
        </Button>
      </div>
    </section>
  );
}

interface Choice {
  readonly id: string;
  readonly label: string;
}

function IssueLine({ issue }: { issue: DraftIssue | undefined }) {
  const { t } = useTranslation();
  if (issue === undefined) return null;
  return (
    <span className="planning-bad" data-testid="planning-scenario-builder-issue">
      {t(`planning.scenarios.builder.issue.${issue}`)}
    </span>
  );
}

/** The fields of one row, by its kind. */
function DraftFields({
  draft,
  issues,
  onChange,
  workCenters,
  shiftNames,
  orders,
  ordersError,
}: {
  draft: HypothesisDraft;
  issues: Issues;
  onChange: (next: HypothesisDraft) => void;
  workCenters: readonly Choice[];
  shiftNames: ReadonlyMap<string, string>;
  orders: readonly { id: string; order_no: string; customer_display_name?: string | null }[];
  ordersError: boolean;
}) {
  const { t } = useTranslation();
  const wcSelect = (value: string, set: (id: string) => void) => (
    <Field label={t("planning.scenarios.builder.workCenter")}>
      <Select
        value={value}
        aria-invalid={issues.workCenterId !== undefined}
        data-testid="planning-scenario-builder-wc"
        onChange={(e) => {
          set(e.target.value);
        }}
      >
        <option value="">
          {workCenters.length === 0
            ? t("planning.scenarios.builder.noWorkCenters")
            : t("planning.scenarios.builder.chooseWorkCenter")}
        </option>
        {workCenters.map((wc) => (
          <option key={wc.id} value={wc.id}>
            {wc.label}
          </option>
        ))}
      </Select>
      <IssueLine issue={issues.workCenterId} />
    </Field>
  );
  const windowFields = (
    starts: string,
    ends: string,
    set: (next: { starts: string; ends: string }) => void,
  ) => (
    <>
      <Field label={t("planning.scenarios.builder.starts")}>
        <input
          type="datetime-local"
          value={starts}
          aria-invalid={issues.starts !== undefined}
          data-testid="planning-scenario-builder-starts"
          onChange={(e) => {
            set({ starts: e.target.value, ends });
          }}
        />
        <IssueLine issue={issues.starts} />
      </Field>
      <Field label={t("planning.scenarios.builder.ends")}>
        <input
          type="datetime-local"
          value={ends}
          aria-invalid={issues.ends !== undefined}
          data-testid="planning-scenario-builder-ends"
          onChange={(e) => {
            set({ starts, ends: e.target.value });
          }}
        />
        <IssueLine issue={issues.ends} />
      </Field>
    </>
  );

  switch (draft.kind) {
    case "add_shift":
      return (
        <div className="planning-scenario-fields">
          {wcSelect(draft.workCenterId, (id) => {
            onChange({ ...draft, workCenterId: id });
          })}
          {windowFields(draft.starts, draft.ends, (w) => {
            onChange({ ...draft, ...w });
          })}
          <Field label={t("planning.scenarios.builder.capacityFactor")}>
            <input
              type="text"
              inputMode="decimal"
              value={draft.capacityFactor}
              aria-invalid={issues.capacityFactor !== undefined}
              data-testid="planning-scenario-builder-factor"
              onChange={(e) => {
                onChange({ ...draft, capacityFactor: e.target.value });
              }}
            />
            <IssueLine issue={issues.capacityFactor} />
          </Field>
        </div>
      );
    case "machine_down":
      return (
        <div className="planning-scenario-fields">
          {wcSelect(draft.workCenterId, (id) => {
            onChange({ ...draft, workCenterId: id });
          })}
          {windowFields(draft.starts, draft.ends, (w) => {
            onChange({ ...draft, ...w });
          })}
        </div>
      );
    case "add_order":
      return (
        <div className="planning-scenario-fields">
          {draft.productId === "" ? (
            <div>
              <ProductPicker
                label={t("planning.scenarios.builder.product")}
                data-testid="planning-scenario-builder-product"
                onPick={(p) => {
                  onChange({ ...draft, productId: p.id, productLabel: `${p.sku} · ${p.name}` });
                }}
              />
              <IssueLine issue={issues.productId} />
            </div>
          ) : (
            <p className="planning-actions" data-testid="planning-scenario-builder-product-chosen">
              <span>
                {t("planning.scenarios.builder.productChosen", { product: draft.productLabel })}
              </span>
              <Button
                size="sm"
                onClick={() => {
                  onChange({ ...draft, productId: "", productLabel: "" });
                }}
              >
                {t("planning.scenarios.builder.changeProduct")}
              </Button>
            </p>
          )}
          <Field label={t("planning.scenarios.builder.qty")}>
            <input
              type="text"
              inputMode="decimal"
              value={draft.qty}
              aria-invalid={issues.qty !== undefined}
              data-testid="planning-scenario-builder-qty"
              onChange={(e) => {
                onChange({ ...draft, qty: e.target.value });
              }}
            />
            <IssueLine issue={issues.qty} />
          </Field>
          <Field label={t("planning.scenarios.builder.dueDate")}>
            <input
              type="date"
              value={draft.dueDate}
              aria-invalid={issues.dueDate !== undefined}
              data-testid="planning-scenario-builder-due"
              onChange={(e) => {
                onChange({ ...draft, dueDate: e.target.value });
              }}
            />
            <IssueLine issue={issues.dueDate} />
          </Field>
          <Field label={t("planning.scenarios.builder.priority")}>
            <Select
              value={draft.priority}
              data-testid="planning-scenario-builder-priority"
              onChange={(e) => {
                onChange({ ...draft, priority: e.target.value });
              }}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={String(n)}>
                  {n === 1
                    ? t("planning.scenarios.builder.priorityRush", { n })
                    : n === 3
                      ? t("planning.scenarios.builder.priorityNormal", { n })
                      : n === 5
                        ? t("planning.scenarios.builder.priorityLow", { n })
                        : String(n)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      );
    case "change_efficiency":
      return (
        <div className="planning-scenario-fields">
          {wcSelect(draft.workCenterId, (id) => {
            onChange({ ...draft, workCenterId: id });
          })}
          <Field label={t("planning.scenarios.builder.shift")}>
            {shiftNames.size > 0 ? (
              <Select
                value={draft.shiftCode}
                aria-invalid={issues.shiftCode !== undefined}
                data-testid="planning-scenario-builder-shift"
                onChange={(e) => {
                  onChange({ ...draft, shiftCode: e.target.value });
                }}
              >
                <option value="">{t("planning.scenarios.builder.chooseShift")}</option>
                {[...shiftNames.entries()].map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </Select>
            ) : (
              <input
                type="text"
                value={draft.shiftCode}
                maxLength={32}
                aria-invalid={issues.shiftCode !== undefined}
                data-testid="planning-scenario-builder-shift"
                onChange={(e) => {
                  onChange({ ...draft, shiftCode: e.target.value });
                }}
              />
            )}
            <IssueLine issue={issues.shiftCode} />
          </Field>
          <Field label={t("planning.scenarios.builder.efficiency")}>
            <input
              type="number"
              min={10}
              max={200}
              step={1}
              value={draft.efficiencyPct}
              aria-invalid={issues.efficiencyPct !== undefined}
              data-testid="planning-scenario-builder-efficiency"
              onChange={(e) => {
                onChange({ ...draft, efficiencyPct: e.target.value });
              }}
            />
            <IssueLine issue={issues.efficiencyPct} />
          </Field>
        </div>
      );
    case "drop_order":
      return (
        <div className="planning-scenario-fields">
          <Field label={t("planning.scenarios.builder.order")}>
            <Select
              value={draft.orderId}
              aria-invalid={issues.orderId !== undefined}
              data-testid="planning-scenario-builder-order"
              onChange={(e) => {
                onChange({ ...draft, orderId: e.target.value, lineId: "" });
              }}
            >
              <option value="">
                {orders.length === 0 && !ordersError
                  ? t("planning.scenarios.builder.noOrders")
                  : t("planning.scenarios.builder.chooseOrder")}
              </option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.customer_display_name
                    ? `${o.order_no} · ${o.customer_display_name}`
                    : o.order_no}
                </option>
              ))}
            </Select>
            {ordersError ? (
              <span className="planning-bad">{t("planning.scenarios.builder.ordersError")}</span>
            ) : null}
            <IssueLine issue={issues.orderId} />
          </Field>
          <OrderLineSelect
            orderId={draft.orderId}
            lineId={draft.lineId}
            issue={issues.lineId}
            onChange={(lineId) => {
              onChange({ ...draft, lineId });
            }}
          />
        </div>
      );
  }
}

/** The chosen order's open lines, each named by its number and product. */
function OrderLineSelect({
  orderId,
  lineId,
  issue,
  onChange,
}: {
  orderId: string;
  lineId: string;
  issue: DraftIssue | undefined;
  onChange: (lineId: string) => void;
}) {
  const { t } = useTranslation();
  const order = useScenarioOrder(orderId === "" ? undefined : orderId);
  const lines = (order.data?.lines ?? []).filter(
    (l) => l.line_status === "open" || l.line_status === "in_production",
  );
  const products = useProductIndex(lines.map((l) => l.product_id));
  return (
    <Field label={t("planning.scenarios.builder.line")}>
      <Select
        value={lineId}
        disabled={orderId === ""}
        aria-invalid={issue !== undefined}
        data-testid="planning-scenario-builder-line"
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        <option value="">{t("planning.scenarios.builder.chooseLine")}</option>
        {lines.map((l) => {
          const p = products.byId.get(l.product_id);
          return (
            <option key={l.id} value={l.id}>
              {t("planning.scenarios.builder.lineOption", {
                line: l.line_no,
                product: p !== undefined ? `${p.sku} · ${p.name}` : "…",
              })}
            </option>
          );
        })}
      </Select>
      <IssueLine issue={issue} />
    </Field>
  );
}
